import { DocumentTextIcon } from '@heroicons/react/outline';
import React from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';

function WorkflowPDF(props) {
  const { tableCols, vals, subs, progress, filled, totalFields, selectedSubs, categoryFillData } =
    props;

  // Font.register({ family: 'Sora', src: 'https://fonts.googleapis.com/css2?family=Sora:wght@600&display=swap' });

  const cellWidth = (1 / tableCols.length).toFixed(5) * 100 + '%';

  const styles = StyleSheet.create({
    tableContainer: {
      padding: 10,
    },

    table: {
      display: 'table',
      borderStyle: 'solid',
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0,
    },

    row: {
      flexDirection: 'row',
    },

    cell: {
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
      padding: 4,
      fontSize: 10,
      width: cellWidth,
    },

    red300: {
      backgroundColor: '#fca5a5',
    },
    red200: {
      backgroundColor: '#fecaca',
    },
    orange200: {
      backgroundColor: '#fed7aa',
    },
    yellow200: {
      backgroundColor: '#fef08a',
    },
    green200: {
      backgroundColor: '#bbf7d0',
    },
    green300: {
      backgroundColor: '#86efac',
    },
    green400: {
      backgroundColor: '#4ade80',
    },

    headerCell: {
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
      padding: 4,
      fontSize: 10,
      width: cellWidth,
      textAlign: 'center',
      backgroundColor: 'lightgray',
    },

    header: {
      marginTop: 6,
      marginBottom: 12,
      // fontFamily: 'Sora'
    },
  });

  function normalizeAnswer(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (Array.isArray(value)) return value.join(',');
    if (typeof value === 'object') {
      if ('value' in value) return normalizeAnswer(value.value);
      try {
        return JSON.stringify(value);
      } catch (e) {
        return '';
      }
    }
    return '';
  }

  function evalValColor(val, i) {
    const chosenOpt = tableCols[i]?.answer_possibilities?.find(
      (opt) => opt?.opt === normalizeAnswer(val && typeof val === 'object' ? val.answer : val)
    );
    if (chosenOpt) {
      return [
        styles.cell,
        StyleSheet.create({
          col: {
            backgroundColor: chosenOpt.color,
          },
        }).col,
      ];
    } else {
      return styles.cell;
    }
  }

  function evalProgressColor(progress) {
    if (progress / totalFields === 0) {
      return [styles.cell, styles.red300];
    } else if (progress / totalFields < 0.35) {
      return [styles.cell, styles.red200];
    } else if (progress / totalFields < 0.5) {
      return [styles.cell, styles.orange200];
    } else if (progress / totalFields < 0.7) {
      return [styles.cell, styles.yellow200];
    } else if (progress / totalFields < 0.9) {
      return [styles.cell, styles.green200];
    } else if (progress / totalFields < 1) {
      return [styles.cell, styles.green300];
    } else {
      return [styles.cell, styles.green400];
    }
  }

  function splitIntoGroups(list, groupSize) {
    const groups = [];
    for (let i = 0; i < list.length; i += groupSize) {
      groups.push(list.slice(i, i + groupSize));
    }
    return groups;
  }

  const PDFTable = () => (
    <Document>
      {splitIntoGroups(vals, 22).map((valGroup, k) => (
        <Page orientation="landscape" style={styles.tableContainer}>
          {/* <Text style={styles.header}>Gesellschaft CGM im Jahr 2022</Text> */}
          <View style={styles.table}>
            <View style={styles.row}>
              <View style={styles.headerCell}>
                <Text>Code</Text>
              </View>

              {tableCols.map((col, colIndex) => (
                <View key={colIndex} style={styles.headerCell}>
                  <Text>{col.topic}</Text>
                </View>
              ))}

              <View style={styles.headerCell}>
                <Text>Filled</Text>
              </View>
              <View style={styles.headerCell}>
                <Text>Final</Text>
              </View>
            </View>
            {valGroup.map(
              (valRow, i) =>
                selectedSubs.includes(subs[i]) && (
                  <View key={i} style={styles.row}>
                    <View key={splitIntoGroups(subs, 22)[k][i]} style={styles.cell}>
                      <Text>{splitIntoGroups(subs, 22)[k][i]}</Text>
                    </View>
                    {valRow.map((val, j) => (
                      <View key={val} style={evalValColor(val, j)}>
                        <Text>
                          {normalizeAnswer(
                            val && typeof val === 'object' ? val.answer : val
                          )}
                        </Text>
                      </View>
                    ))}
                    <View
                      key={i + 'filled-' + splitIntoGroups(filled, 22)[k][i]}
                      style={evalProgressColor(splitIntoGroups(filled, 22)[k][i])}
                    >
                      <Text>
                        {((splitIntoGroups(filled, 22)[k][i] / totalFields) * 100).toFixed(1) + '%'}
                      </Text>
                    </View>
                    <View
                      key={i + 'progress-' + splitIntoGroups(progress, 22)[k][i]}
                      style={evalProgressColor(splitIntoGroups(progress, 22)[k][i])}
                    >
                      <Text>
                        {((splitIntoGroups(progress, 22)[k][i] / totalFields) * 100).toFixed(1) +
                          '%'}
                      </Text>
                    </View>
                  </View>
                )
            )}
          </View>
        </Page>
      ))}

      {/* Category Breakdown Page */}
      {categoryFillData && categoryFillData.length > 0 && (
        <Page orientation="landscape" style={styles.tableContainer}>
          <Text style={styles.header}>Category Fill Breakdown by Subsidiary</Text>
          <View style={styles.table}>
            <View style={styles.row}>
              <View style={[styles.headerCell, { width: '15%' }]}>
                <Text>Code</Text>
              </View>
              {categoryFillData[0] &&
                Object.keys(categoryFillData[0].categories).map((categoryName, index) => (
                  <View
                    key={index}
                    style={[
                      styles.headerCell,
                      { width: `${85 / Object.keys(categoryFillData[0].categories).length}%` },
                    ]}
                  >
                    <Text>{categoryName}</Text>
                  </View>
                ))}
            </View>
            {categoryFillData
              .filter((subData) => selectedSubs.includes(subData.code))
              .slice(0, 20) // Limit to 20 rows per page to fit
              .map((subData, index) => (
                <View key={index} style={styles.row}>
                  <View style={[styles.cell, { width: '15%' }]}>
                    <Text>{subData.code}</Text>
                  </View>
                  {Object.entries(subData.categories).map(
                    ([categoryName, categoryInfo], catIndex) => (
                      <View
                        key={catIndex}
                        style={[
                          styles.cell,
                          { width: `${85 / Object.keys(subData.categories).length}%` },
                          evalProgressColor((categoryInfo.percentage / 100) * totalFields),
                        ]}
                      >
                        <Text>
                          {categoryInfo.filled}/{categoryInfo.total} (
                          {categoryInfo.percentage.toFixed(1)}%)
                        </Text>
                      </View>
                    )
                  )}
                </View>
              ))}
          </View>
        </Page>
      )}

      {/* Category Final Breakdown Page */}
      {categoryFillData && categoryFillData.length > 0 && (
        <Page orientation="landscape" style={styles.tableContainer}>
          <Text style={styles.header}>Category Final Breakdown by Subsidiary</Text>
          <View style={styles.table}>
            <View style={styles.row}>
              <View style={[styles.headerCell, { width: '15%' }]}>
                <Text>Code</Text>
              </View>
              {categoryFillData[0] &&
                Object.keys(categoryFillData[0].categories).map((categoryName, index) => (
                  <View
                    key={index}
                    style={[
                      styles.headerCell,
                      { width: `${85 / Object.keys(categoryFillData[0].categories).length}%` },
                    ]}
                  >
                    <Text>{categoryName}</Text>
                  </View>
                ))}
            </View>
            {categoryFillData
              .filter((subData) => selectedSubs.includes(subData.code))
              .slice(0, 20) // Limit to 20 rows per page to fit
              .map((subData, index) => (
                <View key={index} style={styles.row}>
                  <View style={[styles.cell, { width: '15%' }]}>
                    <Text>{subData.code}</Text>
                  </View>
                  {Object.entries(subData.categories).map(
                    ([categoryName, categoryInfo], catIndex) => (
                      <View
                        key={catIndex}
                        style={[
                          styles.cell,
                          { width: `${85 / Object.keys(subData.categories).length}%` },
                          evalProgressColor((categoryInfo.finalPercentage / 100) * totalFields),
                        ]}
                      >
                        <Text>
                          {categoryInfo.final}/{categoryInfo.total} (
                          {categoryInfo.finalPercentage.toFixed(1)}%)
                        </Text>
                      </View>
                    )
                  )}
                </View>
              ))}
          </View>
        </Page>
      )}
    </Document>
  );

  return (
    <div className="mr-2">
      <PDFDownloadLink document={<PDFTable />} fileName="table.pdf">
        <button className="flex items-center rounded-lg bg-teal-400 text-white px-4 transition-all hover:bg-teal-500 h-[100%]">
          <div className="mr-1">
            <DocumentTextIcon className="w-5 h-5" />
          </div>
          PDF
        </button>
      </PDFDownloadLink>
    </div>
  );
}

export default WorkflowPDF;
